import React from 'react';
import { Truck, RefreshCw, Loader2, AlertTriangle, Ban } from 'lucide-react';
import { Card, CardHeader, CardContent } from './Card';
import { Button } from './Button';
import { formatFreightQuoteCost, formatFreightQuoteDays } from '../utils/freightQuoteDisplay';
import { BLOCKED_OPTION_BADGE, isBlockedQuote, quoteBlockReason } from '../utils/carrierBlocks';

// Painel de cotação de frete — mesmo visual/contrato do painel inline do pedido de
// venda (SalesOrderDetailView), extraído para reuso no Pedido de Entrega (convites).
// O chamador é dono do estado e dos endpoints; este componente só apresenta e delega.

export interface FreightQuoteItem {
  id: number;
  provider_id?: number;
  provider_service_id?: number;
  provider_name?: string;
  provider_service_name?: string;
  cost?: number;
  delivery_days?: number;
  is_selected: boolean;
  is_manual?: boolean;
  /** Transportadora/serviço bloqueado para a empresa (o ERPApi recusa a seleção com 422). */
  blocked?: boolean;
  blocked_reason?: string | null;
}

interface FreightQuotePanelProps {
  quotes: FreightQuoteItem[];
  loading: boolean;
  calculating: boolean;
  saving: boolean;
  error: string | null;
  readonly?: boolean;
  onCalculate: () => void;
  onSelect: (quoteId: number) => void;
}

export const FreightQuotePanel: React.FC<FreightQuotePanelProps> = ({
  quotes, loading, calculating, saving, error, readonly, onCalculate, onSelect,
}) => (
  <Card>
    <CardHeader
      title={
        <span className="flex items-center gap-2">
          <Truck size={16} className="text-emerald-600" />
          Cotação de Frete
        </span>
      }
      action={
        !readonly ? (
          <Button
            size="sm"
            variant="outline"
            icon={calculating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            onClick={onCalculate}
            disabled={calculating || loading}
          >
            {quotes.length > 0 ? 'Recalcular' : 'Calcular Frete'}
          </Button>
        ) : undefined
      }
    />
    <CardContent>
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle size={14} />
          {error}
        </div>
      )}

      {loading || calculating ? (
        <div className="flex items-center justify-center py-8 gap-2 text-slate-400">
          <Loader2 size={20} className="animate-spin" />
          <span>{calculating ? 'Calculando cotações de frete...' : 'Carregando cotações...'}</span>
        </div>
      ) : quotes.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <Truck size={32} className="mx-auto mb-2 opacity-40" />
          <p>Nenhuma cotação de frete disponível.</p>
          <p className="text-xs mt-1">Clique em "Calcular Frete" para obter cotações.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {quotes.map(quote => {
            const isSelected = quote.is_selected;
            const blocked = isBlockedQuote(quote);
            return (
              <label
                key={quote.id}
                title={blocked ? quoteBlockReason(quote) : undefined}
                className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                  isSelected
                    ? 'border-emerald-500 bg-emerald-50'
                    : blocked
                      ? 'border-red-200 bg-red-50/40'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                } ${readonly || saving || blocked ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <input
                  type="radio"
                  name="freight_quote_panel"
                  checked={isSelected}
                  disabled={readonly || saving || blocked}
                  onChange={() => onSelect(quote.id)}
                  className="w-4 h-4 text-emerald-600 border-slate-300 focus:ring-emerald-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{quote.provider_name || `Provedor #${quote.provider_id}`}</span>
                    <span className="text-xs text-slate-500">•</span>
                    <span className="text-sm text-slate-600">{quote.provider_service_name || `Serviço #${quote.provider_service_id}`}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                    <span>Prazo: <strong className="text-slate-700">{formatFreightQuoteDays(quote)}</strong></span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-lg font-bold ${isSelected ? 'text-emerald-700' : 'text-slate-900'}`}>
                    {formatFreightQuoteCost(quote)}
                  </span>
                </div>
                {isSelected && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                    Selecionado
                  </span>
                )}
                {blocked && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"
                    title={quoteBlockReason(quote)}
                  >
                    <Ban size={12} />
                    {BLOCKED_OPTION_BADGE}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </CardContent>
  </Card>
);
